import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Spinner, Textarea } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

// component functions
import * as ComponentFunctions from './Debt.Form.Functions';
import { suggestedCurrencies, validateDebtForm } from "../../CashFlow.Form.Logic";
import { AmountInput, CurrencyChips, FieldLabel, MoreData, PartyChip, Step } from "../../CashFlow.Form.Bits";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";

// components
import { DateTimeInput } from "../../../../commons/component/Inputs/DateTimeInput/DateTimeInput";
import PersonSelectorComponent from "../../../../commons/component/Inputs/PersonSelector/PersonSelector.Component";
import ProtocolSelectorComponent from "../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";

// models
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { NewDebt } from "../../../../models/Entities/CashFlow/Debt/NewDebt";
import { Debt } from "../../../../models/Entities/CashFlow/Debt/Debt";

/**
 * Alta y edición de una deuda. Una de las dos partes es siempre el centro, así
 * que sólo se pregunta por la contraparte; moneda y fecha vienen sugeridas.
 */
const DebtFormComponent = (props: ComponentFunctions.Props) => {

    const isReceivable = props.FormType === ComponentFunctions.FormType.Creditor;
    const isEdit = !!props.CurrentDebt;

    // ── Estado del formulario ──
    const [counterpart, setCounterpart] = useState<Person | undefined>(undefined);
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const [amountText, setAmountText] = useState<string>('');
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [comments, setComments] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);

    const ownerPerson: Person = { personIdentifier: props.OwnerId, id: 0, isDeleted: false, personType: 0, businessName: MEDICAL_CENTER_NAME } as Person;

    // ── Carga ──
    useEffect(() => {
        if (!props.isOpen) return;
        props.GetAllPersons();
        if (props.DebtId && !props.CurrentDebt) props.GetDebtById(props.DebtId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.DebtId]);

    // Al abrir: arranca con lo que ya se sabe (contraparte fija o filtrada, hoy).
    useEffect(() => {
        if (!props.isOpen) return;
        setSubmitted(false);
        setTouched(false);
        if (props.CurrentDebt) return;
        const prefill = props.FixedCounterpart
            ?? (props.PrefillCounterpartId ? ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, props.PrefillCounterpartId) : undefined);
        setCounterpart(prefill);
        setCurrency(undefined);
        setAmountText('');
        setAmount(0);
        setDate(new Date());
        setProtocolId(undefined);
        setComments('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.FixedCounterpart?.personIdentifier, props.PrefillCounterpartId]);

    // Edición: se completa con la deuda.
    useEffect(() => {
        const debt = props.CurrentDebt;
        if (!debt) return;
        const counterpartId = isReceivable ? debt.debtor : debt.creditor;
        setCounterpart(ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, counterpartId));
        setCurrency(debt.currencyCode);
        setAmount(debt.originalAmount);
        setAmountText(String(debt.originalAmount));
        setDate(debt.debtDate ? new Date(debt.debtDate) : undefined);
        setProtocolId(debt.protocolId ?? undefined);
        setComments(debt.comments || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.CurrentDebt?.id, props.Persons.length]);

    useEffect(() => {
        if (props.isSaved && submitted) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaved]);

    // Segundo camino de cierre: terminó de guardar (el spinner bajó) sin error.
    // No depende de que la bandera de éxito cambie de valor, que es lo que
    // dejaba el drawer abierto cuando ya venía en true de un guardado anterior.
    const wasSavingRef = useRef(false);
    useEffect(() => {
        const finished = wasSavingRef.current && !props.isSaving;
        wasSavingRef.current = props.isSaving;
        if (finished && submitted && !props.failOnSaving) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaving, props.failOnSaving]);

    useEffect(() => {
        if (props.failOnSaving) setSubmitted(false);
    }, [props.failOnSaving]);

    // ── Sugerencias ──
    const suggested = useMemo(
        () => suggestedCurrencies({ counterpartId: counterpart?.personIdentifier, debts: props.AllDebts, payments: props.AllPayments }),
        [counterpart?.personIdentifier, props.AllDebts, props.AllPayments]
    );

    useEffect(() => {
        // La moneda se propone sola; si el usuario ya eligió una, se respeta.
        if (!isEdit && !touched) setCurrency(suggested[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggested.join(','), isEdit]);

    // ── Validación ──
    const errors = validateDebtForm({ counterpartId: counterpart?.personIdentifier, currency, amount, date });
    const showError = (key: string) => (submitted ? errors[key] : undefined);
    const canSave = Object.keys(errors).length === 0 && !props.isSaving;

    // ── Guardar ──
    const save = () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0 || !counterpart || !currency || !date) return;

        const debtor = isReceivable ? counterpart.personIdentifier : props.OwnerId;
        const creditor = isReceivable ? props.OwnerId : counterpart.personIdentifier;

        if (!props.CurrentDebt) {
            const newDebt: NewDebt = {
                creditor, debtor, debtDate: date, originalAmount: amount, currencyCode: currency,
                comments: comments || undefined, protocolId: protocolId ?? null
            };
            props.CreateDebt(newDebt);
        } else {
            const debt: Debt = { ...props.CurrentDebt, originalAmount: amount, protocolId: protocolId ?? null, comments: comments || undefined };
            props.UpdateDebt(debt);
        }
    };

    const close = () => { setSubmitted(false); props.onClose(); };

    // ── Textos según el sentido de la deuda ──
    const title = isEdit ? 'Editar deuda' : (isReceivable ? 'Nueva deuda a cobrar' : 'Nueva deuda a pagar');
    const whoQuestion = isReceivable ? '¿Quién nos debe?' : '¿A quién le debemos?';
    const counterpartRole = isReceivable ? 'Deudor' : 'Acreedor';
    const centerRole = isReceivable ? 'Acreedor' : 'Deudor';
    const counterpartName = counterpart ? props.NameOf(counterpart.personIdentifier) : undefined;
    const isCounterpartFixed = !!props.FixedCounterpart;

    const renderForm = () => (
        <div className="cff-root">
            {props.failOnSaving && (
                <div className="cff-banner" role="alert">
                    <strong>No se pudo guardar la deuda.</strong>
                    <span>{props.error?.ErrorMessage || 'Probá de nuevo.'}</span>
                    {props.error?.Errors && props.error.Errors.length > 0 && <ul>{props.error.Errors.map((item) => <li key={item}>{item}</li>)}</ul>}
                </div>
            )}

            {/* 1 · Con quién */}
            <Step n={1} title={whoQuestion} error={showError('counterpart')}
                hint={isEdit || isCounterpartFixed ? undefined : <>El centro es siempre el {centerRole.toLowerCase()}; sólo falta la otra parte.</>}>
                <div className="cff-parties">
                    {isEdit || isCounterpartFixed ? (
                        <PartyChip role={counterpartRole} name={counterpartName || '—'} />
                    ) : (
                        <div className="cff-field" style={{ flex: 1, minWidth: 260 }}>
                            <PersonSelectorComponent
                                personSelected={counterpart ? [counterpart.personIdentifier] : []}
                                selectionLimit={1}
                                isRequired
                                fieldLabel={counterpartRole}
                                CustomAditionalPersonsList={[ownerPerson]}
                                onChangeSelection={(selected: Person[]) => setCounterpart(selected[0])}
                            />
                        </div>
                    )}
                    <span className="cff-parties__arrow" aria-hidden>{isReceivable ? '→' : '←'}</span>
                    <PartyChip role={centerRole} name={MEDICAL_CENTER_NAME} />
                </div>
            </Step>

            {/* 2 · Cuánto */}
            <Step n={2} title="¿Cuánto?" error={showError('amount') || showError('currency')}
                hint={!isEdit && counterpartName && suggested.length > 0 ? <>Moneda sugerida por lo que ya se movió con {counterpartName}.</> : undefined}>
                <div className="cff-field">
                    <FieldLabel text="Moneda" required />
                    <CurrencyChips suggested={suggested} value={currency} onChange={(code) => { setTouched(true); setCurrency(code); }} disabled={isEdit} />
                </div>
                <div className="cff-field">
                    <FieldLabel text="Importe" required help={isEdit && props.CurrentDebt ? `Ya se pagaron ${formatCurrencyAmountWithIssue(props.CurrentDebt.paidAmount, currency)}; el importe no puede ser menor.` : undefined} />
                    <AmountInput id="debt-amount" value={amountText} currency={currency} autoFocus={!isEdit && !!counterpart} error={!!showError('amount')}
                        onChange={(text, value) => { setAmountText(text); setAmount(value); }} />
                    {amount > 0 && currency && <span className="cff-help cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</span>}
                </div>
            </Step>

            {/* 3 · Cuándo */}
            <Step n={3} title="¿De qué fecha?" error={showError('date')}>
                {isEdit ? (
                    <div className="cff-summary">
                        <div className="cff-summary__item"><div className="cff-summary__label">Fecha</div><div className="cff-summary__value cff-num">{date ? moment(date).format('DD/MM/YYYY') : '—'}</div></div>
                        {props.CurrentDebt?.settlementId && <div className="cff-summary__item"><div className="cff-summary__label">Liquidación</div><div className="cff-summary__value">LIQ-{props.CurrentDebt.settlementId}</div></div>}
                    </div>
                ) : (
                    <div className="cff-field" style={{ maxWidth: 260 }}>
                        <div className="cff-input-box">
                            <DateTimeInput label={undefined} placeHolder="Fecha" selectedDate={date} onSelectDate={setDate} isSubmitted={submitted} isRequired errorMessage={undefined} />
                        </div>
                        <span className="cff-help">Por defecto, hoy. La antigüedad de la deuda se cuenta desde acá.</span>
                    </div>
                )}
            </Step>

            <MoreData open={!!protocolId || !!comments}>
                <div className="cff-step">
                    <div className="cff-field">
                        <FieldLabel text="Protocolo" help="Si la deuda pertenece a un estudio." />
                        <ProtocolSelectorComponent
                            selectionLimit={1}
                            selectedProtocols={protocolId === undefined ? [] : [{ id: protocolId } as Protocol]}
                            onChangeSelection={(selected: Protocol[] | undefined) => setProtocolId((selected || [])[0]?.id)}
                        />
                    </div>
                    <div className="cff-field">
                        <FieldLabel text="Comentarios" />
                        <Textarea value={comments} onChange={(_e, data) => setComments(data.value)} resize="vertical" />
                    </div>
                </div>
            </MoreData>
        </div>
    );

    const loadingEdit = !!props.DebtId && !props.CurrentDebt;

    return (
        <OverlayDrawer position="end" open={props.isOpen} size="medium" onOpenChange={(_e, data) => { if (!data.open) close(); }}>
            <DrawerHeader>
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={close} />}>
                    <span className="cff-title">{title}</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {loadingEdit ? <div className="cff-state"><Spinner size="small" /> Cargando deuda…</div> : renderForm()}
            </DrawerBody>
            <DrawerFooter>
                <div className="cff-footer">
                    {amount > 0 && currency && counterpartName && (
                        <span className="cff-footer__summary">
                            {counterpartName} {isReceivable ? 'nos debe' : 'cobra'} <b className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</b>
                        </span>
                    )}
                    <button type="button" className="cff-secondary" onClick={close} disabled={props.isSaving}>Cancelar</button>
                    <button type="button" className="cff-primary" onClick={save} disabled={submitted ? !canSave : props.isSaving}>
                        {props.isSaving && <Spinner size="extra-tiny" />} {isEdit ? 'Guardar cambios' : 'Registrar deuda'}
                    </button>
                </div>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtFormComponent as any);
